import { type ActionData, type FlowData, type InteractionLogEntry } from '../types/interaction-log-data';
import { BaseLogProcessor, type ProcessorResult } from './base-log-processor';
import { fromProcessedInteractionLogs } from '../utils/flow-converter';

export class InteractionProcessor extends BaseLogProcessor<InteractionLogEntry[], InteractionLogEntry> {
  public flows: Map<string, FlowData>;
  private actionConfig: Record<string, { displayName: string; category: string }>;

  constructor() {
    super();
    this.flows = new Map();
    this.actionConfig = {
      MessageParticipant: {
        displayName: 'Play Prompt',
        category: 'communication'
      },
      MessageParticipantIteratively: {
        displayName: 'Loop Prompts',
        category: 'communication'
      },
      InvokeLambdaFunction: {
        displayName: 'Lambda Function',
        category: 'integration'
      },
      TransferContactToAgent: {
        displayName: 'Transfer to Agent',
        category: 'routing'
      },
      GetParticipantInput: {
        displayName: 'Get Participant Input',
        category: 'input'
      }
    };
  }

  public clear(): void {
    this.flows.clear();
  }

  public convertToFlowData(processedLogs: InteractionLogEntry[]): ProcessorResult {
    const flows = Array.from(this.flows.values());
    const sortedFlows = flows.sort((a, b) => a.startTime - b.startTime);
    const { nodes, edges } = fromProcessedInteractionLogs(sortedFlows);

    return {
      nodes,
      edges,
      processedLogs
    };
  }

  public processLogs(logData: InteractionLogEntry[]): InteractionLogEntry[] {
    const logs = logData;
    logs.sort((a, b) => new Date(a.AbsoluteTime).getTime() - new Date(b.AbsoluteTime).getTime());

    this.clear(); // Clear previous flows
    const flowStack: FlowData[] = [];

    for (const log of logs) {
      // Skip logs that don't have LogData (defensive programming)
      if (!log.LogData) {
        continue;
      }

      const timestamp = this.parseTimestamp(log.AbsoluteTime);

      switch (log.EventType) {
        case 'FlowStart':
          this.handleFlowStart(log, flowStack, timestamp);
          break;
        case 'FlowEnd':
          this.handleFlowEnd(log, flowStack, timestamp);
          break;
        case 'Parameters':
          this.handleActionStart(log, timestamp);
          break;
        case 'ExecutionResult':
          this.handleActionEnd(log, timestamp);
          break;
      }
    }

    return logs;
  }

  handleFlowStart(log: InteractionLogEntry, flowStack: FlowData[], timestamp: number): void {
    if (!log.LogData) return;

    const flowName = log.LogData.ResourceName || '';
    const resourceArn = log.LogData.ResourceArn;
    const existingFlow = this.flows.get(resourceArn);

    if (existingFlow && !existingFlow.endTime) {
      flowStack.push(existingFlow);
      return;
    }

    const flow = {
      id: this.generateFlowId(flowName),
      name: flowName,
      type: log.LogData.FlowType || '',
      resourceType: log.LogData.ResourceType || '',
      startTime: timestamp,
      endTime: null,
      duration: 0,
      actions: [],
      resourceArn: resourceArn,
      isModule: log.LogData.ResourceType === 'FlowModule'
    };

    flowStack.push(flow);
    this.flows.set(resourceArn, flow);
  }

  handleFlowEnd(log: InteractionLogEntry, flowStack: FlowData[], timestamp: number): void {
    if (!log.LogData) return;

    const targetArn = log.LogData.ResourceArn;
    const flow = this.flows.get(targetArn);

    if (flow) {
      // Set end time and duration only on first FlowEnd
      if (!flow.endTime) {
        flow.endTime = timestamp;
        flow.duration = this.calculateDuration(flow.startTime, flow.endTime);
      }

      // Preserve meaningful outcomes, don't overwrite with ENDED_FLOW_EXECUTION
      if (
        log.LogData.FlowOutcome &&
        log.LogData.FlowOutcome !== 'ENDED_FLOW_EXECUTION' &&
        (!flow.outcome || flow.outcome === 'ENDED_FLOW_EXECUTION')
      ) {
        flow.outcome = log.LogData.FlowOutcome;
      } else if (!flow.outcome) {
        flow.outcome = log.LogData.FlowOutcome;
      }

      const flowIndex = flowStack.findIndex((f) => f.resourceArn === targetArn);
      if (flowIndex !== -1) {
        flowStack.splice(flowIndex, 1);
      }
    }
  }

  handleActionStart(log: InteractionLogEntry, timestamp: number): void {
    if (!log.LogData) return;

    const targetArn = log.LogData.ResourceArn;
    const targetFlow = this.flows.get(targetArn);
    if (!targetFlow || !log.LogData.Action) return;

    // Generate unique action ID by appending the current count of actions to prevent duplicates
    const actionCount = targetFlow.actions.length;
    const action: ActionData = {
      id: `${log.LogData.Action.Identifier}-${actionCount}`,
      type: log.LogData.Action.Type,
      startTime: timestamp,
      endTime: null,
      duration: 0,
      parameters: log.LogData.Action.Parameters || {}
    };

    targetFlow.actions.push(action);
  }

  handleActionEnd(log: InteractionLogEntry, timestamp: number): void {
    if (!log.LogData) return;

    const targetArn = log.LogData.ResourceArn;
    const targetFlow = this.flows.get(targetArn);
    if (!targetFlow || !log.LogData.Action) return;

    const originalActionId = log.LogData.Action.Identifier;
    const actionType = log.LogData.Action.Type;

    // Find the most recent action with matching original identifier and type that hasn't ended yet
    // Since we now append index to action IDs, we need to match by the original identifier prefix
    const action = targetFlow.actions
      .filter((a) => a.id.startsWith(originalActionId) && a.type === actionType && a.endTime === null)
      .sort((a, b) => b.startTime - a.startTime)[0];

    if (action) {
      action.endTime = timestamp;
      action.duration = this.calculateDuration(action.startTime, action.endTime);
      action.result = log.LogData.Action.ExecutionResult;

      action.displayName = this.getActionDisplayName(action);
      action.category = this.actionConfig[action.type]?.category || 'general';
    }
  }

  getActionDisplayName(action: ActionData): string {
    const config = this.actionConfig[action.type] || {};
    let displayName = config.displayName || action.type;

    if (action.type === 'InvokeLambdaFunction' && typeof action.parameters.LambdaFunctionARN === 'string') {
      const arnParts = action.parameters.LambdaFunctionARN.split(':');
      const functionName = arnParts[arnParts.length - 1];
      displayName = functionName.replace(/^[^-]+-/, '');
    }

    return displayName;
  }
}
